import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { PopperProps } from '@mui/base/Popper';
import type {
	AutocompleteFreeSoloValueMapping,
	AutocompleteGetTagProps,
	AutocompleteGroupedOption,
	UseAutocompleteProps,
} from '@mui/base/useAutocomplete';
import { useAutocomplete } from '@mui/base/useAutocomplete';
import _ from 'lodash';
import type {
	ChangeEvent,
	ClipboardEvent,
	ComponentPropsWithoutRef,
	FocusEvent,
	Key,
	KeyboardEvent,
	MouseEvent,
	ReactNode,
	Ref,
} from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';

import IconChevronDown from '~icons/ci/chevron-down';

import {
	AnimatedPopover,
	notchedPopperModifier,
	Tag,
} from '@adflow/frontend/ui-common';
import { su, theme, typography } from '@adflow/frontend/ui-theme';
import {
	useForwardingRef,
	useWindowViewHeight,
} from '@adflow/frontend/util-common';

import { Checkbox } from '../Checkbox';
import type { InputProps, InputStatus } from '../Inputs';
import { selectorStyles } from '../Selectors';
import {
	limitGroupedOptions,
	limitOptions,
} from '../use-autocomplete-helpers/limit-options';

const warningMessageStyle = css(typography.regular, su.textXs, {
	color: theme.colors.text.errorText,
	padding: '0.25rem 0.75rem',
	position: 'sticky',
	marginTop: -4,
	top: -4,
	backgroundColor: theme.colors.background.paper,
	zIndex: 1,
});

export interface MultiselectProps<
	T,
	FreeSolo extends boolean | undefined = false,
>
	extends
		InputStatus,
		Pick<InputProps, 'placeholder'>,
		Pick<
			UseAutocompleteProps<T, true, false, FreeSolo>,
			| 'freeSolo'
			| 'options'
			| 'getOptionLabel'
			| 'getOptionDisabled'
			| 'isOptionEqualToValue'
			| 'value'
			| 'inputValue'
			| 'onInputChange'
			| 'onOpen'
			| 'onClose'
			| 'filterOptions'
			| 'groupBy'
			| 'open'
		> {
	isOptionEqualToValue?: (
		option: T,
		value: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => boolean;
	onChange: (value: (T | AutocompleteFreeSoloValueMapping<FreeSolo>)[]) => void;
	popoverRef?: Ref<HTMLDivElement>;
	inputRef?: Ref<HTMLInputElement>;
	testid?: string;
	/**
	 * `name` is not placed on the input. This prop is for compatibility with an
	 * old component that use `name` instead of `testid`.
	 * @deprecated Use `testid` instead.
	 */
	name?: string;
	maxListSize?: number;
	canSearchForMore?: boolean;
	maxSelectedCount?: number;
	/**
	 * For display validation errors
	 * (e.g. "only 5 tags allowed")
	 */
	itemName?: string;

	isLoading?: boolean;
	styles?: {
		listbox?: CSSInterpolation;
		input?: CSSInterpolation;
	};
	slotProps?: { input?: Partial<ComponentPropsWithoutRef<'input'>> };

	popoverStyle?: CSSInterpolation;

	getOptionColor?: (
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => string | undefined;
	getOptionKey?: (
		i: number,
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => string;
	getOptionGlobal?: (
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => boolean;
	getOptionSortKey?: (
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => number | string | null | undefined;
	renderTag?: (props: MultiselectRenderTagProps<T, FreeSolo>) => ReactNode;
	renderGroup?: (props: MultiselectRenderGroupProps) => ReactNode;
	renderOption?: (props: MultiselectRenderOptionProps<T>) => ReactNode;
	renderSelectedItems?: (
		props: MultiselectRenderSelectedItemsProps<T, FreeSolo>
	) => ReactNode;
	sortSelectedToTop?: boolean;
	emptyMessage?: string;
	dropdownTopContainer?: ReactNode;
	dropdownBottomContainer?: ReactNode;
	showAllNoneButtons?: boolean;
	/**
	 * @deprecated Multiselect now always closes on select unless a checkbox is
	 *             used. This prop is ignored.
	 */
	closeOnSelect?: true;
	popperProps?: Partial<PopperProps>;
	endAdornment?: ReactNode;
	clearOnBlur?: boolean;

	// TODO: Does not work with grouped options yet.
	reactWindow?: boolean;

	disableCloseOnSelect?: boolean;
	onCheckboxToggle?: () => void;
}

/**
 * - Select a single option from a list of options.
 * - The options can be filtered by typing in the input.
 */
export function MultiselectControlled<
	T,
	FreeSolo extends boolean | undefined = false,
>({
	// InputStatus
	error,
	archived,
	readOnly,
	disabled,
	open,

	// InputProps
	placeholder,

	// UseAutocompleteProps
	freeSolo,
	options,
	getOptionLabel = (option) => '' + option,
	getOptionKey = (i, option) => `${i}-${getOptionLabel(option)}`,
	getOptionDisabled = () => false,
	getOptionGlobal = () => false,
	getOptionColor,
	getOptionSortKey,
	isOptionEqualToValue = (option, value) => option === value,
	value: valueProp,
	onChange,
	inputValue: propsInputValue,
	onInputChange,
	onOpen,
	onClose,
	filterOptions,
	groupBy,

	// other
	popoverRef,
	inputRef: propsInputRef,
	name,
	testid = _.kebabCase(name),
	maxListSize = 50,
	canSearchForMore,
	maxSelectedCount,
	itemName,

	popoverStyle,

	isLoading,
	styles,
	slotProps,

	renderTag = defaultRenderTag,
	renderGroup = defaultRenderGroup,
	renderOption = defaultRenderOption,
	renderSelectedItems,
	emptyMessage,
	showAllNoneButtons,
	dropdownTopContainer,
	dropdownBottomContainer,
	popperProps = MultiselectBasePopperProps,
	endAdornment,
	clearOnBlur = false,
	reactWindow = false,
	disableCloseOnSelect = false,
	onCheckboxToggle,
	sortSelectedToTop = false,
}: MultiselectProps<T, FreeSolo>) {
	const { t } = useTranslation(['common', 'validations']);

	// get height of the window
	const pageHeight = useWindowViewHeight();

	const {
		getRootProps,
		getInputProps,
		getListboxProps,
		getOptionProps,
		groupedOptions,
		anchorEl,
		setAnchorEl,
		popupOpen,
		getPopupIndicatorProps,
		getTagProps,
		inputValue,
		value,
	} = useAutocomplete<T, true, false, FreeSolo>({
		// Always close on normal select. The user can use the checkboxes to select
		// multiple at once without closing the popover.
		disableCloseOnSelect,
		openOnFocus: true,
		clearOnBlur,
		multiple: true,
		open,
		freeSolo,
		filterSelectedOptions: false,

		options,
		getOptionLabel: (...args) => getOptionLabel(...args) ?? '',
		getOptionDisabled: (option) => canSelectItem || getOptionDisabled(option),

		isOptionEqualToValue,
		value: valueProp,
		onChange: (_event, newValue) => {
			onChange(
				maxSelectedCount ? newValue.slice(0, maxSelectedCount) : newValue
			);
		},
		disabled,
		readOnly,

		inputValue: propsInputValue,
		onInputChange,
		onOpen,
		onClose,
		filterOptions,
		groupBy,
	});

	const canSelectItem: boolean = maxSelectedCount
		? value.length >= maxSelectedCount
		: false;

	const renderLimitedNotice = () => (
		<li css={selectorStyles.emptyListItem}>{t('typeForMoreResults')}</li>
	);

	const getOptionColorInternal = (
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => getOptionColor?.(option) ?? theme.colors.background.tag.blue;

	const toggleAll = () => {
		if (value.length >= (maxSelectedCount || options.length)) {
			onChange([]);
		} else {
			onChange([...options]);
		}
	};

	const renderOptionInternal = (
		option: T,
		index: number,
		style?: React.CSSProperties
	) => {
		const selected = value.some((v) => isOptionEqualToValue(option, v));
		const key = getOptionKey(index, option);
		const optionProps = getOptionProps({ option, index });
		const disabled_ = optionProps['aria-disabled'] === true;

		return renderOption({
			key,
			optionProps: {
				...optionProps,
				style: {
					...optionProps.style,
					...style,
				},
			},
			checkboxProps: {
				type: 'checkbox',
				checked: selected,
				// The checkbox is inside the option, but we do not want the option's
				// `onClick` to be called because the checkbox should have different
				// behavior. When the checkbox is clicked, we do not want to close the
				// popover.
				onClick: (e) => {
					e.stopPropagation();
				},
				onChange: (e) => {
					const newValue = e.target.checked
						? [...value, option]
						: value.filter((v) => !isOptionEqualToValue(option, v));
					onCheckboxToggle?.();
					onChange(
						maxSelectedCount ? newValue.slice(0, maxSelectedCount) : newValue
					);
				},
				disabled: optionProps['aria-disabled'] === true,
			},
			label: getOptionLabel(option),
			color: getOptionColorInternal(option),
			global: getOptionGlobal(option),
			option,
			selected,
			inputValue,
			disabled: disabled_,
		});
	};

	const _renderOptions = (options_: T[], style?: React.CSSProperties) =>
		options_.map((option, index) => renderOptionInternal(option, index, style));

	const renderGroups = (groups: AutocompleteGroupedOption<T>[]) =>
		groups.map(({ key, group, options: options_, index }) =>
			renderGroup({
				key,
				group,
				children: options_.map((option2, index2) =>
					renderOptionInternal(option2, index + index2)
				),
			})
		);

	const inputProps = getInputProps();
	const mergedInputProps = {
		...inputProps,
		...slotProps?.input,
		onBlur: (e: FocusEvent<HTMLInputElement>) => {
			inputProps.onBlur?.(e);
			slotProps?.input?.onBlur?.(e);
		},
		onChange: (e: ChangeEvent<HTMLInputElement>) => {
			inputProps.onChange?.(e);
			slotProps?.input?.onChange?.(e);
		},
		onClick: (e: MouseEvent<HTMLInputElement>) => {
			inputProps.onClick?.(e);
			slotProps?.input?.onClick?.(e);
		},
		onFocus: (e: FocusEvent<HTMLInputElement>) => {
			inputProps.onFocus?.(e);
			slotProps?.input?.onFocus?.(e);
		},
		onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
			inputProps.onKeyDown?.(e);
			slotProps?.input?.onKeyDown?.(e);
		},
		onMouseDown: (e: MouseEvent<HTMLInputElement>) => {
			inputProps.onMouseDown?.(e);
			slotProps?.input?.onMouseDown?.(e);
		},
		onPaste: (e: ClipboardEvent<HTMLInputElement>) => {
			inputProps.onPaste?.(e);
			slotProps?.input?.onPaste?.(e);
		},
	};
	const inputRef = useForwardingRef<null | HTMLInputElement>(null, [
		mergedInputProps.ref,
		propsInputRef,
	]);

	const selectedItems = renderSelectedItems?.({
		value,
		getTagProps,
		getOptionLabel,
		getOptionColor: getOptionColorInternal,
		getOptionGlobal,
		renderTag,
		inputProps: mergedInputProps,
		inputRef,
		popupOpen,
		placeholder,
		disabled,
		readOnly,
		styles,
		testid,
	});

	return (
		<div {...getRootProps()}>
			<div
				ref={setAnchorEl}
				css={[
					selectorStyles.multiselectContainer({
						error: !!error,
						disabled,
						archived,
						readOnly,
					}),
					{
						padding: 0,
						display: 'grid',
						gridTemplateColumns: '1fr auto',
						alignItems: 'stretch',
						justifyContent: 'stretch',
						gap: 0,
					},
				]}
			>
				{selectedItems ?? (
					<div
						data-testid={`${testid}-selected-items`}
						css={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							justifyContent: 'flex-start',
							gap: '0.25rem',
							paddingLeft: '0.25rem',
							paddingTop: '0.125rem',
							paddingBottom: '0.125rem',
							minHeight: '2.5rem',
							minWidth: 0,
						}}
					>
						{value.map((tag, index) => {
							const { onDelete, ...tagProps } = getTagProps({ index });

							return renderTag({
								tagProps,
								onDelete: disabled || readOnly ? undefined : onDelete,

								label: getOptionLabel(tag),
								color: getOptionColorInternal(tag),
								global: getOptionGlobal(tag),
								option: tag,

								testid,
							});
						})}
						<input
							{...mergedInputProps}
							ref={inputRef}
							placeholder={value?.length ? undefined : placeholder}
							css={[
								selectorStyles.multiselectInput({
									disabled,
									archived,
									readOnly,
								}),
								// Fade the input value when the popup is closed so that it does not
								// look like the search text is part of the selected value.
								popupOpen
									? undefined
									: {
											// Same color as input placeholders.
											color: theme.colors.text.hint,
										},
								styles?.input,
							]}
							disabled={disabled}
							readOnly={readOnly}
							data-testid={`${testid}-input`}
						/>
					</div>
				)}
				<div css={{ display: 'flex' }}>
					{readOnly ? null : (
						<button
							{...getPopupIndicatorProps()}
							type="button"
							disabled={disabled}
							css={{
								color: theme.colors.input.adornment,
								cursor: 'pointer',

								width: '2.5rem',
								height: '2.5rem',

								// Center the icon in the button.
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
							data-testid={`${testid}-button`}
						>
							<IconChevronDown
								css={[
									disabled &&
										css({
											color: theme.colors.text.disabled,
										}),
									popupOpen && { rotate: '180deg' },
								]}
								width={20}
								height={20}
							/>
						</button>
					)}
					{endAdornment}
				</div>
			</div>
			<AnimatedPopover
				ref={popoverRef}
				anchorEl={anchorEl}
				open={popupOpen}
				PopperProps={popperProps}
				css={[selectorStyles.popover(), popoverStyle]}
				data-testid={`${testid}-popover`}
			>
				<ul
					{...getListboxProps()}
					css={[
						selectorStyles.list,
						{
							// The element that gets `getListboxProps` must also be the
							// element that scrolls, so that the selected option can be
							// scrolled into view.
							maxHeight: '40vh',
							overflow: 'auto',
						},
						styles?.listbox,
					]}
				>
					{showAllNoneButtons ? (
						<button
							css={[
								typography.clickableLink,
								su.textSm,
								{ marginBottom: '0.25rem', marginLeft: '0.5rem' },
							]}
							onClick={toggleAll}
							type="button"
							data-cy="toggle-all-perms"
						>
							{t('Select all/none')}
						</button>
					) : null}
					{dropdownTopContainer}

					{maxSelectedCount && value.length >= maxSelectedCount ? (
						<div css={warningMessageStyle}>
							{t('validations:Max Items', {
								maxCount: maxSelectedCount,
								itemName,
							})}
						</div>
					) : null}

					{groupedOptions.length <= 0 ? (
						isLoading ? (
							<li css={[selectorStyles.emptyListItem, typography.emptySmall]}>
								{t('loading')}
							</li>
						) : (
							<li css={selectorStyles.emptyListItem}>
								{emptyMessage ?? t('noResults')}
							</li>
						)
					) : groupBy ? (
						(() => {
							const { limitedGroups, limited } = limitGroupedOptions(
								groupedOptions as AutocompleteGroupedOption<T>[],
								maxListSize
							);

							return (
								<>
									{renderGroups(limitedGroups)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>
							);
						})()
					) : (
						(() => {
							const { limitedOptions, limited } = limitOptions(
								groupedOptions as T[],
								maxListSize
							);
							const orderedOptionsData = (() => {
								if (!sortSelectedToTop) {
									return limitedOptions.map((option, idx) => ({
										option,
										originalIndex: idx,
									}));
								}

								const selectedOptions: { option: T; originalIndex: number }[] =
									[];
								const unselectedOptions: {
									option: T;
									originalIndex: number;
								}[] = [];
								for (const [i, option] of limitedOptions.entries()) {
									if (value.some((v) => isOptionEqualToValue(option, v))) {
										selectedOptions.push({ option, originalIndex: i });
									} else {
										unselectedOptions.push({ option, originalIndex: i });
									}
								}

								if (getOptionSortKey) {
									const withKey: { option: T; originalIndex: number }[] = [];
									const withoutKey: { option: T; originalIndex: number }[] = [];
									for (const item of selectedOptions) {
										const key = getOptionSortKey(item.option);
										if (key === null || key === undefined) {
											withoutKey.push(item);
										} else {
											withKey.push(item);
										}
									}

									withKey.sort((a, b) => {
										const aKey = getOptionSortKey(a.option)!;
										const bKey = getOptionSortKey(b.option)!;
										if (typeof aKey === 'number' && typeof bKey === 'number') {
											return aKey - bKey;
										}
										return String(aKey).localeCompare(String(bKey));
									});

									return [...withKey, ...withoutKey, ...unselectedOptions];
								}

								return [...selectedOptions, ...unselectedOptions];
							})();

							if (reactWindow) {
								// 40% of the viewport height, because parent component set to "maxHeight: '40vh'"
								// 8px is for the padding of the parent
								const height = Math.min(330, pageHeight * 0.4 - 8);

								// Width is the width of the anchor element minus 2px for the border
								const width = anchorEl?.offsetWidth
									? anchorEl?.offsetWidth - 2
									: 300;

								return (
									<FixedSizeList
										height={height}
										width={width}
										itemCount={orderedOptionsData.length}
										itemSize={40}
										innerElementType="ul"
									>
										{({ index, style }) => {
											const item = orderedOptionsData[index];
											if (!item) return null;
											const { option, originalIndex } = item;
											return renderOptionInternal(option, originalIndex, style);
										}}
									</FixedSizeList>
								);
							}

							return (
								<>
									{orderedOptionsData.map(({ option, originalIndex }) =>
										renderOptionInternal(option, originalIndex)
									)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>
							);
						})()
					)}
				</ul>
				{dropdownBottomContainer}
			</AnimatedPopover>
		</div>
	);
}

export const MultiselectBasePopperProps: Partial<PopperProps> = {
	placement: 'bottom-start',
	modifiers: [
		{ name: 'offset', options: { offset: [0, -2] } },
		notchedPopperModifier({ notchDepth: 1 }),
	],
};

export interface MultiselectRenderTagProps<
	T,
	FreeSolo extends boolean | undefined = false,
> {
	tagProps: Omit<ReturnType<AutocompleteGetTagProps>, 'onDelete'>;
	onDelete?: ReturnType<AutocompleteGetTagProps>['onDelete'];

	global: boolean;
	label: string;
	color: string;
	option: T | AutocompleteFreeSoloValueMapping<FreeSolo>;

	testid?: string;
}

function defaultRenderTag<T, FreeSolo extends boolean | undefined = false>({
	tagProps: { key, ...tagProps },
	onDelete,
	label,
	color,
	global,
	testid,
}: MultiselectRenderTagProps<T, FreeSolo>) {
	return (
		<Tag
			key={key}
			{...tagProps}
			css={onDelete === undefined ? undefined : { cursor: 'pointer' }}
			text={label}
			color={color}
			global={global}
			size="clickable"
			removable={onDelete !== undefined}
			onClick={onDelete}
			data-testid={`${testid}-selected-item`}
		/>
	);
}

export interface MultiselectRenderGroupProps {
	key: Key;
	group: string;
	children?: ReactNode;
}

function defaultRenderGroup({
	key,
	group,
	children,
}: MultiselectRenderGroupProps) {
	return (
		<li key={key}>
			<div
				css={[
					typography.regular,
					{ color: theme.colors.text.secondary, padding: '0.5rem 0.75rem' },
				]}
			>
				{group}
			</div>
			<ul css={{ '& > li': { paddingLeft: '1.5rem' } }}>{children}</ul>
		</li>
	);
}

export interface MultiselectRenderOptionProps<T> {
	key: string;
	optionProps: ComponentPropsWithoutRef<'li'>;
	checkboxProps: ComponentPropsWithoutRef<'input'>;
	global: boolean;
	label: string;
	color: string;
	option: T;
	selected: boolean;
	inputValue: string;
	disabled?: boolean;
}

export interface MultiselectRenderSelectedItemsProps<
	T,
	FreeSolo extends boolean | undefined = false,
> {
	value: (T | AutocompleteFreeSoloValueMapping<FreeSolo>)[];
	getTagProps: AutocompleteGetTagProps;
	getOptionLabel: (
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => string;
	getOptionColor: (
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => string;
	getOptionGlobal: (
		option: T | AutocompleteFreeSoloValueMapping<FreeSolo>
	) => boolean;
	renderTag: (props: MultiselectRenderTagProps<T, FreeSolo>) => ReactNode;
	inputProps: ComponentPropsWithoutRef<'input'>;
	inputRef: Ref<HTMLInputElement>;
	popupOpen: boolean;
	placeholder?: string;
	disabled?: boolean;
	readOnly?: boolean;
	styles?: {
		listbox?: CSSInterpolation;
		input?: CSSInterpolation;
	};
	testid?: string;
}

function defaultRenderOption<T>({
	key,
	optionProps: { key: optionKey, ...optionProps },
	checkboxProps: { key: checkboxKey, ...checkboxProps },
	label,
	color,
	global,
	inputValue,
	disabled,
}: MultiselectRenderOptionProps<T>) {
	return (
		<li
			key={key ?? optionKey}
			{...optionProps}
			onClick={(e) => {
				if (!disabled) {
					optionProps.onClick?.(e);
				}
			}}
			css={[selectorStyles.listItem, { display: 'flex', paddingLeft: 0 }]}
		>
			<Checkbox
				key={checkboxKey}
				{...checkboxProps}
				containerStyle={{
					width: '2.5rem',
					height: '2rem',
					minWidth: '2.5rem',
				}}
			/>

			<Tag
				text={label}
				color={color}
				global={global}
				searchTerm={inputValue}
				size="clickable"
			/>
		</li>
	);
}

/**
 * This wrapper component makes sure input value is not cleared until the dropdown is closed
 * and input value is not cleared out when selecting options using checkboxes, but it does not take
 * inputValue as a prop, so it is not possible to control the input value from outside.
 * If you need to control the input value from outside, use `MultiselectControlled` instead.
 */
export function Multiselect<T, FreeSolo extends boolean | undefined = false>({
	onClose,
	onInputChange,
	...props
}: Omit<MultiselectProps<T, FreeSolo>, 'inputValue'>) {
	const [stateInputValue, setStateInputValue] = useState('');

	return (
		<MultiselectControlled
			{...props}
			inputValue={stateInputValue}
			onClose={(event, reason) => {
				onClose?.(event, reason);
				setStateInputValue('');
			}}
			onInputChange={(e, value, reason) => {
				onInputChange?.(e, value, reason);
				setStateInputValue(value);
			}}
		/>
	);
}
